import type { NoteTopicId, Relations } from "@inrev/common";
import { useUpdateAdminNoteTopic } from "@portal/api/note/admin";
import { cn } from "@portal/components/lib/utils";
import { Icon } from "@portal/components/primitives";
import { useOnClickOutside } from "@portal/hooks/useOnClickOutside";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef, useState } from "react";
import { filterXSS } from "xss";

export const AdminNoteSummarySection = ({
	relation,
	topicId,
	topicSummary,
	allowUpdate,
}: {
	relation: Relations.Notes;
	topicId: NoteTopicId;
	topicSummary: string;
	allowUpdate: boolean;
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const { updateAdminNoteTopic } = useUpdateAdminNoteTopic();

	const wrapperRef = useRef<HTMLDivElement>(null);

	useOnClickOutside(wrapperRef, () => setIsEditing(false));

	const editor = useEditor(
		{
			editable: true,
			content: topicSummary,
			shouldRerenderOnTransaction: true,
			extensions: [
				StarterKit.configure({
					bold: {
						HTMLAttributes: {
							class: "font-[550]",
						},
					},
					link: false,
					listKeymap: false,
				}),
				Placeholder.configure({
					placeholder: "Enter a summary...",
					emptyEditorClass:
						"before:h-0 before:float-left before:content-[attr(data-placeholder)] before:text-gray-400 before:italic cursor-text",
				}),
			],
			editorProps: {
				attributes: {
					class: "outline-none focus:outline-none py-[12px] px-[16px] min-h-[100px] text-[14px]",
				},
			},
			onBlur: () => {
				if (editor?.getHTML()) {
					if (
						topicSummary === editor.getHTML() ||
						(editor.getHTML() === "<p></p>" && topicSummary === "")
					) {
						return;
					}
					updateAdminNoteTopic({
						data: {
							summary: editor.getHTML(),
						},
						relation,
						noteTopicId: topicId,
					});
					topicSummary = editor.getHTML();
				}
				setIsEditing(false);
			},
		},
		[topicSummary],
	);

	// Used to focus the end of the editor when the user double clicks on the summary
	useEffect(() => {
		if (isEditing && editor) {
			editor.commands.focus("end");
		}
	}, [isEditing]);

	return (
		<div
			className={cn("flex flex-col gap-[16px] w-full min-h-fit")}
			onDoubleClick={() => {
				allowUpdate && setIsEditing(true);
			}}
		>
			<p className={cn("text-gray-400 text-[12px]")}>Summary</p>
			{!isEditing && (
				<div
					className="text-gray-800 text-[13px] text-[375] cursor-default select-none"
					dangerouslySetInnerHTML={{
						__html:
							topicSummary.length > 0 && topicSummary !== "<p></p>"
								? filterXSS(topicSummary)
								: "<p>--</p>",
					}}
				/>
			)}
			{isEditing && editor && (
				<div
					tabIndex={1}
					className={cn(" bg-white rounded-md border border-slate-200 !mx-[-12px]")}
				>
					<div className="flex items-center h-[32px] px-[4px] space-x-[3px] border-b border-b-gray-100 justify-between bg-gray-50 rounded-t-md">
						<div className="flex items-center space-x-[3px] min-w-fit">
							<div
								onMouseDown={(e) => {
									e.preventDefault();
									e.stopPropagation();
									editor.chain().focus().toggleBold().run();
								}}
								className={cn(
									"flex items-center justify-center w-[26px] h-[26px] rounded text-gray-700 hover:bg-gray-100 hover:text-primary cursor-pointer",
									editor.isActive("bold")
										? "bg-gray-200/80 hover:bg-gray-200/80 text-primary"
										: "hover:bg-gray-100 hover:text-primary",
								)}
							>
								<Icon type="bold" size="mini" />
							</div>
							<div
								onMouseDown={(e) => {
									e.preventDefault();
									e.stopPropagation();
									editor.chain().focus().toggleItalic().run();
								}}
								className={cn(
									"flex items-center justify-center w-[26px] h-[26px] rounded text-gray-700 hover:bg-gray-100 hover:text-primary cursor-pointer",
									editor.isActive("italic")
										? "bg-gray-200/80 hover:bg-gray-200/80 text-primary"
										: "hover:bg-gray-100 hover:text-primary",
								)}
							>
								<Icon type="italic" size="mini" className="mt-[-.75px]" />
							</div>
							<div
								onMouseDown={(e) => {
									e.preventDefault();
									e.stopPropagation();
									editor.chain().focus().toggleUnderline().run();
								}}
								className={cn(
									"flex items-center justify-center w-[26px] h-[26px] rounded text-gray-700 hover:bg-gray-100 hover:text-primary cursor-pointer",
									editor.isActive("underline")
										? "bg-gray-200/80 hover:bg-gray-200/80 text-primary"
										: "hover:bg-gray-100 hover:text-gray-800",
								)}
							>
								<Icon type="underline" size="micro" className="mt-[1px]" />
							</div>
							<div
								onMouseDown={(e) => {
									e.preventDefault();
									e.stopPropagation();
									editor.chain().focus().toggleBulletList().run();
								}}
								className={cn(
									"flex items-center justify-center w-[26px] h-[26px] rounded text-gray-700 hover:bg-gray-100 hover:text-primary cursor-pointer",
									editor.isActive("bulletList")
										? "bg-gray-200/80 hover:bg-gray-200/80 text-primary"
										: "hover:bg-gray-100 hover:text-gray-800",
								)}
							>
								<Icon type="list-unordered" size="small" className="mt-[1px] mb-[1px]" />
							</div>
							<div
								onMouseDown={(e) => {
									e.preventDefault();
									e.stopPropagation();
									editor.chain().focus().toggleOrderedList().run();
								}}
								className={cn(
									"flex items-center justify-center w-[26px] h-[26px] rounded text-gray-700 hover:bg-gray-100 hover:text-primary cursor-pointer",
									editor.isActive("orderedList")
										? "bg-gray-200/80 hover:bg-gray-200/80 text-primary"
										: "hover:bg-gray-100 hover:text-gray-800",
								)}
							>
								<Icon type="list-ordered" size="small" className="mt-[1px] mb-[1px]" />
							</div>
						</div>
					</div>
					<EditorContent editor={editor} ref={wrapperRef} />
				</div>
			)}
		</div>
	);
};
